import { createRouter, createWebHistory } from 'vue-router';
import { authState } from '@/store/auth';
import { accessState } from '@/store/access';
import analyticsService from '@/services/analytics';
import NProgress from 'nprogress';
import i18n from '@/plugins/i18n';

NProgress.configure({
  showSpinner: false,
  minimum: 0.1,
  speed: 500,
  trickleSpeed: 200,
  easing: 'ease-out',
  template: '<div class="bar" role="bar"><div class="peg"></div></div>'
});

const Register = () => import('@/components/view/UserRegister.vue');
const Login = () => import('@/components/view/UserLogin.vue');
const SmartSearch = () => import('@/components/view/SmartSearch/SmartSearch.vue');
const SavedNames = () => import('@/components/view/SavedNames.vue');
const ManageUsers = () => import('@/components/view/ManageUsers/ManageUsers.vue');
const ConfirmEmail = () => import('@/components/view/ConfirmEmail.vue');
const LandingPage = () => import('@/components/view/Landing/LandingPage.vue');
const AboutPage = () => import('@/components/view/About/AboutPage.vue');
const UserProfile = () => import('@/components/view/UserProfile/UserProfile.vue');
const MailboxView = () => import('@/components/view/Mailbox/MailboxView.vue');
const ReferralCapture = () => import('@/views/ReferralCapture.vue');
const PasswordResetRequest = () => import('@/components/view/PasswordResetRequest.vue');
const PasswordReset = () => import('@/components/view/PasswordReset.vue');
const MarketResearchView = () => import('@/components/view/MarketResearch/MarketResearch.vue');
const MarketResearchResponses = () => import('@/components/view/MarketResearch/MarketResearchResponses.vue');
const MarketResearchDiscountResponse = () => import('@/components/view/MarketResearch/DiscountRequestResponse.vue');
const MyAnswers = () => import('@/components/view/MyAnswers.vue');
const DomesticProducers = () => import('@/components/view/DomesticProducers/DomesticProducers.vue');
const NeolinkSearchIframeView = () => import('@/views/NeolinkSearchIframeView.vue');
const NeolinkSmartSearch = () => import('@/components/view/SmartSearchForNeolink/NeolinkSmartSearch.vue');
const PricingPage = () => import('@/components/view/Pricing/PricingPage.vue');
const PaymentSuccess = () => import('@/components/view/PaymentSuccess.vue');
const PaymentFailure = () => import('@/components/view/PaymentFailure.vue');
const UserAgreementPage = () => import('@/components/view/UserAgreementPage.vue');
const PrivacyPolicyPage = () => import('@/components/view/PrivacyPolicyPage.vue');
const PublicSmartSearchView = () => import('@/components/view/SmartSearch/PublicSmartSearchView.vue');
const SpecChat = () => import('@/components/view/SpecChat/SpecChat.vue');
const Forbidden = () => import('@/components/view/ForbiddenPage.vue');

const routes = [
  {
    path: '/',
    component: LandingPage,
    meta: { titleKey: 'landing' }
  },
  {
    path: '/about',
    name: 'About',
    component: AboutPage,
    meta: { titleKey: 'about' }
  },
  {
    path: '/my-answers',
    name: 'MyAnswers',
    component: MyAnswers,
    meta: { titleKey: 'my_answers' }
  },
  {
    path: '/domestic-producers',
    name: 'DomesticProducers',
    component: DomesticProducers,
    meta: {
      requiresAuth: true,
      requiresPermission: 'domestic_producer_view',
      titleKey: 'domestic_producers'
    },
  },
  {
    path: '/suppliers',
    name: 'SuppliersPage',
    component: () => import('@/components/view/Suppliers/SuppliersPage.vue'),
    meta: {
      titleKey: 'suppliers',
      requiresAuth: true
    }
  },
  {
    path: '/suppliers/:bin_iin',
    name: 'SupplierDetail',
    component: () => import('@/components/view/Suppliers/SupplierDetailPage.vue'),
    meta: {
      titleKey: 'suppliers',
      requiresAuth: true
    }
  },
  {
    path: '/enstru-search',
    name: 'EnstruSearch',
    component: () => import('@/components/view/EnstruSearch/EnstruSearch.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'enstru_search'
    },
  },
  {
    path: '/enstru-search/import/:id',
    name: 'EnstruImportView',
    component: () => import('@/components/view/EnstruSearch/EnstruImportView.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'enstru_import_view'
    }
  },
  {
    path: '/reset/request',
    name: 'PasswordResetRequest',
    component: PasswordResetRequest,
    meta: { titleKey: 'password_reset_request' }
  },
  {
    path: '/reset_password/:token',
    name: 'PasswordReset',
    component: PasswordReset,
    props: true,
    meta: { titleKey: 'password_reset' }
  },
  {
    path: '/ref/:token',
    name: 'ReferralCapture',
    component: ReferralCapture,
    props: true,
    meta: { titleKey: 'register' }
  },
  {
    path: '/profile',
    component: UserProfile,
    meta: {
      requiresAuth: true,
      titleKey: 'profile'
    }
  },
  {
    path: '/mail',
    component: MailboxView,
    meta: {
      requiresAuth: true,
      titleKey: 'mailbox'
    }
  },
  {
    path: '/register',
    component: Register,
    meta: {
      requiresGuest: true,
      titleKey: 'register'
    },
  },
  {
    path: '/login',
    component: Login,
    meta: {
      requiresGuest: true,
      titleKey: 'login'
    },
  },
  {
    path: '/smart_search',
    component: SmartSearch,
    meta: {
      requiresAuth: true,
      titleKey: 'smart_search'
    },
  },
  {
    // Вторая версия создания спецификации — диалог с ИИ. Мастер в SmartSearch остается.
    path: '/spec/:guid?',
    name: 'SpecChat',
    component: SpecChat,
    meta: {
      requiresAuth: true,
      // Страница занимает все окно под навбаром: футер не нужен, запас под
      // мобильную панель она учитывает сама.
      hideFooter: true,
      fillViewport: true,
      titleKey: 'spec_chat'
    },
  },
  // {
  //   path: '/bulk-ts-generation',
  //   name: 'BulkTsGeneration',
  //   component: () => import('@/components/view/BulkTsGeneration/BulkTsGeneration.vue'),
  //   meta: {
  //     requiresAuth: true,
  //     titleKey: 'bulk_ts_generation'
  //   },
  // },
  // {
  //   path: '/ai-specification-agent',
  //   name: 'AiSpecificationAgent',
  //   component: () => import('@/components/view/AiSpecificationAgent/AiSpecificationAgent.vue'),
  //   meta: {
  //     requiresAuth: true,
  //     titleKey: 'ai_specification_agent'
  //   },
  // },
  {
    path: '/confirm/:token',
    name: 'ConfirmEmail',
    component: ConfirmEmail,
    // Без requiresGuest: ссылка из письма должна открываться и в браузере,
    // где уже есть активная сессия, иначе подтверждение молча теряется.
    meta: {
      titleKey: 'confirm_email'
    },
  },
  {
    path: '/smart_search/public/:guid',
    name: 'PublicSmartSearch',
    component: PublicSmartSearchView,
    props: true,
    meta: { titleKey: 'smart_search_public' }
  },
  {
    path: '/saved-names',
    component: SavedNames,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'saved_names'
    },
  },
  {
    path: '/manage-users',
    name: 'ManageUsers',
    component: ManageUsers,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'manage_users'
    },
  },
  {
    path: '/user-tags',
    name: 'UserTags',
    component: () => import('@/views/UserTags.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'user_tags'
    }
  },
  {
    path: '/parser-settings',
    name: 'ParserSettings',
    component: () => import('@/components/view/ParserSettings/ParserSettings.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'parser_settings'
    }
  },
  {
    path: '/admin/organizations',
    name: 'AdminOrganizations',
    component: () => import('@/components/view/Admin/AdminOrganizations.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'admin_organizations' // Need to add to locales
    }
  },
  {
    path: '/admin/apikeys',
    name: 'AdminApiKeys',
    component: () => import('@/components/view/Admin/AdminApiKeys.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'admin_api_keys'
    }
  },
  {
    path: '/admin/system-directories',
    alias: '/admin/system-directories/kato',
    name: 'SystemDirectories',
    component: () => import('@/components/view/Admin/SystemDirectories.vue'),
    // ?dir= переключает вкладку справочника, страницу при этом не пересоздаём.
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Системные справочники', keepOnQueryChange: true }
  },
  {
    path: '/admin/abc-catalog',
    name: 'AdminAbcCatalog',
    component: () => import('@/components/view/AbcAdmin/AbcAdminView.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      titleKey: 'abc_catalog'
    }
  },
  {
    // Все открытые проработки: поставщик находит запрос и отвечает в
    // гостевой форме. Страница публичная, контакты заказчиков — после входа.
    path: '/open-researches',
    name: 'OpenResearches',
    component: () => import('@/components/view/OpenResearches/OpenResearchesPage.vue'),
    meta: { titleKey: 'open_researches', keepOnQueryChange: true }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: PricingPage,
    meta: { titleKey: 'pricing' }
  },
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: PaymentSuccess,
    meta: { titleKey: 'payment_success' }
  },
  {
    path: '/payment/failure',
    name: 'PaymentFailure',
    component: PaymentFailure,
    meta: { titleKey: 'payment_failure' }
  },
  {
    path: '/user-agreement',
    name: 'UserAgreement',
    component: UserAgreementPage,
    meta: { titleKey: 'user_agreement' }
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyPage,
    meta: { titleKey: 'privacy_policy' }
  },
  {
    path: '/my-nomenclature',
    name: 'MyNomenclature',
    component: () => import('@/components/view/MyNomenclature/MyNomenclature.vue'),
    redirect: { name: 'MyNomenclatureAll' },
    meta: { requiresAuth: true, requiresPermission: 'nomenclature.view', titleKey: 'my_nomenclature' },
    children: [
      {
        path: 'list',
        name: 'MyNomenclatureAll',
        component: () => import('@/components/view/MyNomenclature/components/AllItemsView.vue'),
        meta: { requiresAuth: true, titleKey: 'my_nomenclature' }
      },
      {
        path: 'history',
        name: 'MyNomenclatureHistory',
        component: () => import('@/components/view/MyNomenclature/components/ImportHistoryView.vue'),
        meta: { requiresAuth: true, titleKey: 'my_nomenclature' }
      }
    ]
  },
  {
    path: '/my-standards',
    name: 'MyStandards',
    component: () => import('@/components/view/MyStandards/MyStandards.vue'),
    redirect: { name: 'MyStandardsDistribution' },
    meta: { requiresAuth: true, requiresPermission: 'nomenclature.view', titleKey: 'my_standards' },
    children: [
      {
        path: 'distribution',
        name: 'MyStandardsDistribution',
        component: () => import('@/components/view/MyNomenclature/components/DistributionView.vue'),
        meta: { requiresAuth: true, titleKey: 'my_standards' }
      },
      {
        path: 'list',
        name: 'MyStandardsList',
        component: () => import('@/components/view/MyStandards/GroupsStandardsView.vue'),
        meta: { requiresAuth: true, titleKey: 'my_standards' }
      },
      {
        path: 'catalog',
        name: 'MyStandardsCatalog',
        component: () => import('@/components/view/MyStandards/CategoryCatalogView.vue'),
        meta: { requiresAuth: true, titleKey: 'my_standards' }
      }
    ]
  },
  {
    path: '/abc-standards',
    name: 'AbcStandards',
    component: () => import('@/components/view/AbcStandards/AbcStandards.vue'),
    meta: { requiresAuth: true, requiresPermission: 'nomenclature.view', titleKey: 'abc_standards' }
  },
  {
    // Раздел открыт всем пользователям организации — ролевой проверки нет
    // намеренно, requiresPermission здесь не ставится.
    path: '/research-analytics',
    name: 'ResearchAnalytics',
    component: () => import('@/components/view/ResearchAnalytics/ResearchAnalytics.vue'),
    meta: { requiresAuth: true, titleKey: 'research_analytics' }
  },
  {
    path: '/research-analytics/:key',
    name: 'ResearchAnalyticsDetail',
    component: () => import('@/components/view/ResearchAnalytics/ResearchAnalyticsDetail.vue'),
    meta: { requiresAuth: true, titleKey: 'research_analytics' }
  },
  {
    path: '/unreliable-suppliers',
    name: 'UnreliableSuppliers',
    component: () => import('@/views/UnreliableSuppliers/UnreliableSuppliersPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'unreliable_suppliers'
    }
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: Forbidden,
    meta: { titleKey: 'forbidden' }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
  {
    path: '/market_research/:id',
    name: 'MarketResearchView',
    component: MarketResearchView,
    props: true,
    // ?tab= переключает вкладки автора — страницу при этом не пересоздаём,
    // иначе теряются фильтры поставщиков и загруженные данные.
    meta: { titleKey: 'market_research_view', waitForAuth: true, keepOnQueryChange: true }
  },
  {
    path: '/market_research/:id/responses',
    name: 'MarketResearchResponses',
    component: MarketResearchResponses,
    props: true,
    meta: { titleKey: 'market_research_view', waitForAuth: true }
  },
  {
    // Ответ поставщика на запрос скидки — по токену из письма, гостем или
    // авторизованным пользователем; правила доступа как у обычного ответа.
    path: '/market_research/:id/discount/:token',
    name: 'MarketResearchDiscountResponse',
    component: MarketResearchDiscountResponse,
    props: true,
    meta: { titleKey: 'market_research_view', waitForAuth: true }
  },
  {
    path: '/neolink-search-iframe',
    name: 'NeolinkSearchIframe',
    component: NeolinkSearchIframeView,
    meta: {
      hideNavbar: true,
      allowIframe: true,
      titleKey: 'neolink_search_iframe'
    }
  },
  {
    path: '/neolink',
    name: 'Neolink',
    component: NeolinkSmartSearch,
    meta: {
      hideNavbar: true,
      titleKey: 'neolink_smart_search'
    }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Если есть сохраненная позиция (например, при нажатии кнопки "Назад")
    if (savedPosition) {
      return savedPosition;
    }
    // Если есть хеш в URL (для якорных ссылок)
    if (to.hash) {
      return {
        el: to.hash
      };
    }
    // Та же страница, сменился только query (фильтры, вкладки) — прокрутку не трогаем
    if (to.path === from.path) {
      return false;
    }
    // По умолчанию мгновенно перемещаемся к верху страницы
    return {
      top: 0,
      left: 0
    };
  }
});

router.beforeEach(async (to, from, next) => {
  if (to.path !== from.path) {
    NProgress.start();
  }

  if (to.meta.allowIframe) {
    if (typeof document !== 'undefined') {
      document.head.querySelector('meta[http-equiv="Content-Security-Policy"]')?.remove();
      const meta = document.createElement('meta');
      meta.setAttribute('http-equiv', 'Content-Security-Policy');
      meta.setAttribute('content', 'frame-ancestors *');
      document.head.appendChild(meta);
    }
    next();
    return;
  }

  // Force Russian language for Market Research Respondent View (Guest)
  // If no language is saved in localStorage, default to Russian regardless of browser settings
  if (['MarketResearchView', 'MarketResearchDiscountResponse'].includes(to.name) && !authState.isAuthenticated) {
    const savedLang = localStorage.getItem('user-language');
    if (!savedLang) {
      console.log('Forcing Russian for Guest Market Research (Router Guard)');
      if (i18n.global.locale.value) {
        i18n.global.locale.value = 'ru';
      } else {
        i18n.global.locale = 'ru';
      }
      localStorage.setItem('user-language', 'ru');
      document.documentElement.lang = 'ru';
    }
  }

  // Определяем, нужно ли ждать инициализации аутентификации для этого роута
  const needsAuthWait = to.meta.requiresAuth ||
    to.meta.requiresGuest ||
    to.meta.requiresAdmin ||
    to.meta.requiresAdminOrAtamekenAdmin ||
    to.meta.requiresPermission ||
    to.meta.waitForAuth;

  // Ждем завершения инициализации только для защищенных роутов
  if (needsAuthWait && !authState.isInitialized) {
    // Создаем Promise, который разрешится когда инициализация завершится
    await new Promise((resolve) => {
      const checkInitialization = () => {
        if (authState.isInitialized) {
          resolve();
        } else {
          // Проверяем каждые 50ms
          setTimeout(checkInitialization, 50);
        }
      };
      checkInitialization();
    });
  }

  if (to.meta.requiresAuth && !authState.isAuthenticated) {
    next('/login');
  } else if (to.meta.requiresGuest && authState.isAuthenticated) {
    next('/smart_search');
  } else if (to.meta.requiresAdmin && authState.role !== 'admin') {
    next({ name: 'Forbidden', query: { from: to.fullPath } });
  } else if (to.meta.requiresAdminOrAtamekenAdmin &&
    !(authState.role === 'admin' || authState.role === 'atameken_admin')) {
    next({ name: 'Forbidden', query: { from: to.fullPath } });
  } else if (to.meta.requiresPermission && authState.isAuthenticated && !accessState.loaded) {
    try {
      await accessState.load();
    } catch (_) {
      next({ name: 'Forbidden', query: { from: to.fullPath } });
      return;
    }
    if (!accessState.can(to.meta.requiresPermission)) {
      next({ name: 'Forbidden', query: { from: to.fullPath } });
    } else {
      next();
    }
  } else if (to.meta.requiresPermission &&
    !accessState.can(to.meta.requiresPermission)) {
    next({ name: 'Forbidden', query: { from: to.fullPath } });
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  if (to.path !== from.path) {
    setTimeout(() => {
      NProgress.done();
    }, 150);
  }

  setTimeout(() => {
    // Title
    let pageTitle = 'QuickPrice';
    if (to.meta.titleKey) {
      const localizedTitle = i18n.global.t(`page_titles.${to.meta.titleKey}`);
      pageTitle = localizedTitle || `${to.meta.titleKey} - QuickPrice`;
    } else if (to.meta.title) {
      pageTitle = to.meta.title;
    }

    document.title = pageTitle;

    // Description (i18n-aware)
    let description = '';
    if (to.meta.titleKey) {
      const localizedDescription = i18n.global.t(`meta_descriptions.${to.meta.titleKey}`);
      if (localizedDescription && typeof localizedDescription === 'string') {
        description = localizedDescription;
      }
    }
    if (!description) {
      description = i18n.global.t('meta_descriptions.main') || 'QuickPrice - сервис быстрого анализа цен и маркетинговых исследований';
    }

    // Helper для установки/обновления метатега
    const upsertMeta = (nameOrProp, content, isProperty = false) => {
      if (!content) return;
      const selector = isProperty ? `meta[property="${nameOrProp}"]` : `meta[name="${nameOrProp}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) el.setAttribute('property', nameOrProp); else el.setAttribute('name', nameOrProp);
        document.head.appendChild(el);
      }
      el.setAttribute('content', String(content));
    };

    // Canonical
    const baseUrl = window.location.origin;
    const canonicalHref = `${baseUrl}${to.fullPath.split('#')[0]}`;
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref);

    // hreflang (ru, en, kk, zh если доступны)
    const locales = ['ru', 'en', 'kk', 'zh'];
    // Удаляем старые hreflang перед добавлением
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(n => n.remove());
    locales.forEach((loc) => {
      const href = `${baseUrl}${to.fullPath.split('?')[0]}`;
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', loc);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    });
    // x-default
    const xdef = document.createElement('link');
    xdef.setAttribute('rel', 'alternate');
    xdef.setAttribute('hreflang', 'x-default');
    xdef.setAttribute('href', canonicalHref);
    document.head.appendChild(xdef);

    // Open Graph
    upsertMeta('og:title', pageTitle, true);
    upsertMeta('og:description', description, true);
    upsertMeta('og:type', 'website', true);
    upsertMeta('og:url', canonicalHref, true);
    const ogImage = `${baseUrl}/og-image.png`;
    upsertMeta('og:image', ogImage, true);
    upsertMeta('og:image:width', '1200', true);
    upsertMeta('og:image:height', '630', true);

    // Twitter
    upsertMeta('twitter:card', 'summary');
    upsertMeta('twitter:title', pageTitle);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', ogImage);

    // Standard description
    upsertMeta('description', description);

    // Смена только параметров на той же странице (фильтры, вкладки ?tab=) —
    // не новый просмотр: иначе каждая галочка фильтра шла событием в аналитику.
    const sameView = to.path === from.path && to.name === from.name && to.meta.keepOnQueryChange;
    if (!sameView) {
      analyticsService.trackPageView(to.fullPath, pageTitle, to.name || to.meta.titleKey || '');
    }
  }, 100);
});

router.onError((error) => {
  console.error('Router error:', error);
  NProgress.done();
});

export default router;
